/* eslint-disable no-nested-ternary  */
// biome-ignore lint/style/useImportType: keeping value import for compatibility
import * as React from 'react';
import {
  type Dispatch,
  type FC,
  Fragment,
  memo,
  type SetStateAction,
} from 'react';

import {
  createUrlWithSourceQueryParams,
  useAmplitude,
} from '@crehana/analytics';
import ArrowIosDownOutline from '@crehana/icons/ArrowIosDownOutline';
import ArrowIosUpOutline from '@crehana/icons/ArrowIosUpOutline';
import CloseSmallOutline from '@crehana/icons/CloseSmallOutline';
import MenuOutline from '@crehana/icons/MenuOutline';
import Phone from '@crehana/icons/Phone';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { PrimaryButton } from '@crehana/ui';
import { primaryMain, white } from '@crehana/ui/dist/styles/colors';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';

import { baseRedirectUrlToContactForm } from '@/views/B2BLanding/constants';
import { AMPLITUDE_TRACKS, EVENTS } from '@/views/B2BLanding/events';
import { buildStaticResourceUtms } from '@/views/B2BLanding/utils/buildStaticResourceUtms';
import { joinQueryParams } from '@/views/B2BLanding/utils/joinQueryParams';

// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { BaseMainMenuTypes } from '../../Header.models';
import useHeaderTranslation from '../../hooks/useHeaderTranslation';

interface RightProps extends BaseMainMenuTypes {
  darkMode: boolean;
  toggleOpenMobile: () => void;
  setOpenConfirmationDialog: Dispatch<SetStateAction<boolean>>;
}

const Right: FC<React.PropsWithChildren<RightProps>> = ({
  darkMode = false,
  items,
  itemOpen,
  toggleItem,
  showCallButton,
  toggleOpenMobile,
  isMobileOpen,
  setOpenContactDialog,
}) => {
  const router = useRouter();
  const { logEvent } = useAmplitude();

  const { t } = useHeaderTranslation();

  const getFreeTrialUrl = () => {
    if (typeof window !== 'undefined') {
      console.log('getFreeTrialUrl');
      return createUrlWithSourceQueryParams({
        url: joinQueryParams(
          baseRedirectUrlToContactForm,
          router.asPath,
          buildStaticResourceUtms({
            urlResource: router.query.slug as string,
            term: 'agenda-demo',
          }),
        ),
        source: 'Header - Agenda una demo',
      });
    }
    return '';
  };

  return (
    <Fragment>
      <div className="items-center hidden lg:flex">
        {items.map(item => {
          const isOpen = itemOpen === item.key;
          const isCollapsible = item.type === 'items' || item.type === 'groups';

          return (
            <div key={item.id}>
              <button
                type="button"
                className={clsx(
                  'font-link font-extrabold text-14 mx-16 px-8 flex items-center cursor-pointer',
                  isOpen && isCollapsible
                    ? 'text-primary-main'
                    : darkMode
                      ? 'text-white'
                      : 'text-base-main',
                )}
                onClick={() => {
                  if (!item.data.link) {
                    toggleItem(item.key);
                  } else {
                    logEvent({
                      event: EVENTS[item.key as keyof typeof EVENTS],
                      data: {},
                    });
                    router.push(item.data.link);
                  }
                }}
                aria-label={t(item.name)}
              >
                {t(item.name)}
                {isCollapsible &&
                  (isOpen ? (
                    <ArrowIosUpOutline
                      className="ml-4"
                      color={primaryMain}
                      size={16}
                    />
                  ) : (
                    <ArrowIosDownOutline
                      color={darkMode ? white : undefined}
                      className="ml-4"
                      size={16}
                    />
                  ))}
              </button>
            </div>
          );
        })}
        {showCallButton ? (
          <PrimaryButton
            leftIcon={{
              component: Phone,
              size: 24,
            }}
            onClick={() => {
              logEvent({
                event: AMPLITUDE_TRACKS.MAIN_HEADER_CALL_US,
                data: {
                  'CTA Type': 'button',
                  'CTA Location': 'header',
                  'CTA Text': t('HEADER_CALL_BUTTON'),
                },
              });
              setOpenContactDialog(true);
            }}
            label={t('HEADER_CALL_BUTTON')}
            className="mx-8"
            size="s"
          />
        ) : (
          <Fragment>
            <PrimaryButton
              white={darkMode}
              onClick={() => {
                logEvent({
                  event: AMPLITUDE_TRACKS.WANTS_TO_KNOW_MORE,
                  data: {
                    'CTA Type': 'button',
                    'CTA Location': 'header',
                    'CTA Text': t('HEADER_FREE_TRIAL_BUTTON'),
                  },
                });
                router.push(getFreeTrialUrl());
              }}
              label={t('HEADER_FREE_TRIAL_BUTTON')}
              className="mx-8"
              size="s"
            />
          </Fragment>
        )}
      </div>
      <div className="block lg:hidden">
        <button
          type="button"
          onClick={() => {
            toggleOpenMobile();
          }}
          aria-label="toggle-menu"
        >
          {isMobileOpen ? (
            <CloseSmallOutline color={darkMode ? white : undefined} />
          ) : (
            <MenuOutline color={darkMode ? white : undefined} />
          )}
        </button>
      </div>
    </Fragment>
  );
};

export default memo(Right);
